import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import logger from '@/utils/logger';

interface UserPreferencesRPC {
  user_id: string;
  active_company_id: string | null;
  active_company: {
    id: string;
    name: string;
    created_at: string;
  } | null;
  created_at: string | null;
  updated_at: string | null;
}

export function useUserPreferencesRPC() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch user preferences using RPC
  const { data: preferences, isLoading, error } = useQuery({
    queryKey: ['userPreferencesRPC', user?.id],
    queryFn: async () => {
      if (!user) return null;

      logger.info('Fetching user preferences via RPC');
      
      const { data, error } = await supabase.rpc('get_user_preferences');
      
      if (error) {
        logger.error('Error fetching user preferences:', error);
        throw error;
      }
      
      logger.info('User preferences fetched:', data);
      return data as UserPreferencesRPC;
    },
    enabled: !!user,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Switch active company mutation using RPC
  const switchActiveCompanyMutation = useMutation({
    mutationFn: async (companyId: string) => {
      logger.info('Switching active company via RPC to:', companyId);
      
      const { data, error } = await supabase.rpc('switch_active_company', {
        p_company_id: companyId
      });

      if (error) {
        logger.error('Switch active company RPC error:', error);
        throw error;
      }
      
      logger.info('Switch active company RPC response:', data);
      return data;
    },
    onSuccess: (data) => {
      // Update the preferences in the cache
      queryClient.setQueryData(['userPreferencesRPC', user?.id], (oldData: UserPreferencesRPC | null) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          active_company_id: data.company.id,
          active_company: data.company,
          updated_at: new Date().toISOString(),
        };
      });

      // Invalidate to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['userPreferencesRPC', user?.id] });
      
      logger.info('Successfully switched active company via RPC', data.company);
    },
    onError: (error) => {
      logger.error('Failed to switch active company via RPC', error);
    },
  });

  return {
    preferences,
    isLoading,
    error,
    activeCompanyId: preferences?.active_company_id || null,
    activeCompany: preferences?.active_company || null,
    switchActiveCompany: (companyId: string) => switchActiveCompanyMutation.mutate(companyId),
    isSwitching: switchActiveCompanyMutation.isPending,
  };
}